import moment from "moment";
import { getLedgerYears } from "@/api/carteraAnalista";
import {
    getReportExportDownload,
    getReportExportStatus,
    startReportExport,
} from "@/api/financialReport";
import { useImportRunPolling, ImportRunPollTimeout } from "@/composables/useImportRunPolling";
import { downloadUrl } from "@/utils/downloadBlob";
import { consoleHandlerError, extractErrorMessage, notificate } from "@/utils/message";

// Export del reporte a xlsx (decisiones §34).
export const EXPORT_IDLE = "idle";
export const EXPORT_RUNNING = "running";
export const EXPORT_ERROR = "error";
export const EXPORT_STILL_RUNNING_MESSAGE =
    "El archivo sigue generándose. Vuelve a intentar en unos minutos.";
export const EXPORT_FAILED_MESSAGE =
    "No se pudo generar el archivo. Intenta de nuevo en unos minutos.";
// Vive en el módulo, no en un componente: el polling sigue aunque el usuario
// cambie de tab mientras el archivo se genera.
const exportPolling = useImportRunPolling({ intervalMs: 3000, maxAttempts: 100 });

// La corrida terminó en error: el backend ya dejó el motivo legible en `error`.
export class ReportExportFailed extends Error {
    constructor(message) {
        super(message || EXPORT_FAILED_MESSAGE);
        this.name = "ReportExportFailed";
    }
}

// Cada lectura del polling: el `progress` del job ({progress, stage_detail})
// o null mientras el worker todavía no lo tomó.
export function exportTick(commit, status) {
    const progress = status && status.progress;
    commit("SET_EXPORT_PROGRESS", progress
        ? { percent: progress.progress ?? 0, sheet: progress.stage_detail || "" }
        : null);
}

function exportErrorMessage(error) {
    if (error instanceof ImportRunPollTimeout) return EXPORT_STILL_RUNNING_MESSAGE;
    if (error instanceof ReportExportFailed) return error.message;
    return error?.response?.data?.error || extractErrorMessage(error, EXPORT_FAILED_MESSAGE);
}

// Filtros compartidos entre los tabs de reportes (Resultados, Ventas, Gastos).
// Cualquier filtro que tenga que aplicarse de forma transversal entre los tabs
// vive acá. Hoy: el mes de referencia del calendario y el rango de período.

// Rangos que forzan una fecha-fin canónica distinta del endMonth actual.
// Cuando el caller selecciona uno de estos rangos, también sincronizamos
// endMonth para que el calendario y la consulta queden coherentes entre sí.
// Para agregar un nuevo rango con override (previousMonth, previousQuarter…)
// basta con sumar la entrada — la action no cambia.
//
// `previousYear` solía vivir aquí — lo movimos a `resolveDateRange` para que
// el rango sea self-contained y no se rompa cuando el usuario cambia
// endMonth desde el calendario después de haber elegido "Año Anterior".
const RANGE_OVERRIDES = {};

// Persistencia del rango -------------------------------------------------
// Cambiar de empresa hace window.location.reload() (userAuth/setActiveCompany),
// que reinicia TODO el state Vuex — incluido este módulo. Para que el rango de
// meses elegido sea el MISMO al cambiar de empresa, lo persistimos en
// sessionStorage y sembramos el state al inicializar. sessionStorage (no
// localStorage) es a propósito: sobrevive al reload dentro de la sesión del tab
// pero no arrastra un rango viejo a sesiones futuras (al cerrar el tab vuelve a
// los defaults). `pendingDrill` NO se persiste (es transitorio, cross-tab).
const STORAGE_KEY = "reportsFilters";

function reviveDate(value) {
    return value ? moment(value).toDate() : value;
}

function reviveRange(range) {
    return range && range.from && range.to
        ? { from: reviveDate(range.from), to: reviveDate(range.to) }
        : range;
}

function loadPersisted() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const data = JSON.parse(raw);
        const out = {};
        if (data.endMonth) out.endMonth = reviveDate(data.endMonth);
        if (data.selectedPeriodRange) {
            out.selectedPeriodRange = data.selectedPeriodRange;
        }
        if ("customRange" in data) out.customRange = reviveRange(data.customRange);
        if ("customYearRange" in data) {
            out.customYearRange = reviveRange(data.customYearRange);
        }
        return out;
    } catch (e) {
        return {};
    }
}

function persist(state) {
    try {
        // Date.prototype.toJSON serializa a ISO; reviveDate lo revive al cargar.
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                endMonth: state.endMonth,
                selectedPeriodRange: state.selectedPeriodRange,
                customRange: state.customRange,
                customYearRange: state.customYearRange,
            })
        );
    } catch (e) {
        /* noop — sessionStorage no disponible */
    }
}

const state = () => ({
    endMonth: moment().toDate(),
    selectedPeriodRange: "lastYear",
    // Rango explícito definido por el usuario via AnualCalendar en modo
    // "range" (hoy sólo Ventas). Cuando viene { from, to }, el caller debe
    // setear selectedPeriodRange = "custom" para que `resolveDateRange` lo use
    // en lugar del rango predefinido. Las fechas son Date objects normalizadas
    // al primer día del mes.
    customRange: null,
    // Rango de años explícito elegido en el AnualCalendar (granularidad "year",
    // sólo tabs en modo range). `{ from: Date, to: Date }` normalizado a Ene 1
    // del año. Estado SEPARADO de `customRange` a propósito: la selección de
    // meses (Mensual/Trimestral) y la de años (Anual) no deben pisarse al
    // togglear de período — cada una persiste su propia ventana. `resolveDateRange`
    // sólo lo aplica cuando filterPeriod === "year". null = sin selección
    // (cae al default de "últimas DEFAULT_ANNUAL_YEARS columnas de año").
    customYearRange: null,
    // Drill cross-tab: cuando el usuario clickea una celda del P&L (Resultados)
    // queremos saltar al tab de esa fila y abrir su modal del mes. La URL hace
    // el salto, pero el tab destino monta limpio y no recibe el contexto del
    // click. Este "buzón" guarda { tab, monthLabel }; el tab destino lo consume
    // en su `mounted` (abre el modal) y lo limpia. null = sin drill pendiente.
    pendingDrill: null,
    // Primer mes visible en los reportes (Date, día 1) — piso de los
    // calendarios de TODOS los tabs: meses/años anteriores se deshabilitan.
    // Es el MAYOR entre los dos pisos que devuelve ledger_years: `first_month`
    // (derivado de los documentos, regla mayor↔SII del cutoff) y
    // `report_start_month` (fijado por el analista; el backend ya recorta los
    // datos de la web a ese mes). null = sin dato (sin documentos, sin piso
    // manual o fetch fallido) → sin piso, mismo comportamiento que hoy. NO se
    // persiste: es data de la empresa, no una elección del usuario, y se
    // refetchea en cada carga.
    dataFirstMonth: null,
    // true cuando loadDataAvailability terminó (con o sin dato). Gate del
    // montaje de los tabs en DashboardReports: sin esto el fetch inicial de
    // cada tab corría en paralelo con la carga de dataFirstMonth y el primer
    // render mostraba el rango sin recortar. No se persiste.
    dataAvailabilityReady: false,
    // Botón "Exportar": estado de la corrida y avance 0-100 con la hoja en
    // curso que publica la task (null hasta el primer tick). Declarados acá
    // para que sean reactivos (Vue 2 no observa claves agregadas después);
    // no se persisten: una corrida es de la sesión.
    exportState: EXPORT_IDLE,
    exportProgress: null,
    // Sobrescribe los defaults de arriba con lo persistido (si hay), para que
    // el rango sobreviva al reload del cambio de empresa.
    ...loadPersisted(),
});

const mutations = {
    SET_END_MONTH(state, date) {
        state.endMonth = date;
        persist(state);
    },
    SET_SELECTED_PERIOD_RANGE(state, range) {
        state.selectedPeriodRange = range;
        persist(state);
    },
    SET_CUSTOM_RANGE(state, payload) {
        state.customRange = payload;
        persist(state);
    },
    SET_CUSTOM_YEAR_RANGE(state, payload) {
        state.customYearRange = payload;
        persist(state);
    },
    SET_PENDING_DRILL(state, payload) {
        // Transitorio (buzón cross-tab) — no se persiste.
        state.pendingDrill = payload;
    },
    SET_DATA_FIRST_MONTH(state, payload) {
        // Data de la empresa (no elección del usuario) — no se persiste.
        state.dataFirstMonth = payload;
    },
    SET_EXPORT_STATE(state, payload) {
        state.exportState = payload;
    },
    SET_EXPORT_PROGRESS(state, payload) {
        state.exportProgress = payload;
    },
    SET_DATA_AVAILABILITY_READY(state, payload) {
        state.dataAvailabilityReady = payload;
    },
};

const actions = {
    setPeriodRange({ commit }, range) {
        commit("SET_SELECTED_PERIOD_RANGE", range);
        const overrideFn = RANGE_OVERRIDES[range];
        if (overrideFn) commit("SET_END_MONTH", overrideFn());
    },
    // Aplica un rango personalizado: switchea selectedPeriodRange a "custom",
    // sincroniza endMonth al `to` del rango (mantiene la coherencia con el
    // calendar pill y con cualquier consumer que mire endMonth aislado) y
    // guarda el rango. `from`/`to` deben ser Date objects.
    setCustomRange({ commit }, range) {
        if (!range || !range.from || !range.to) return;
        commit("SET_CUSTOM_RANGE", range);
        commit("SET_SELECTED_PERIOD_RANGE", "custom");
        commit("SET_END_MONTH", range.to);
    },
    // Aplica un rango de años (granularidad "year"). A diferencia de
    // `setCustomRange`, NO sincroniza endMonth ni selectedPeriodRange: el span
    // de años es ortogonal al mes de referencia que usan Mensual/Trimestral/YTD,
    // y pisarlos desplazaría sus defaults. `resolveDateRange` lee este estado
    // sólo cuando filterPeriod === "year". `from`/`to` deben ser Date objects.
    setCustomYearRange({ commit }, range) {
        if (!range || !range.from || !range.to) return;
        commit("SET_CUSTOM_YEAR_RANGE", range);
    },
    // Carga el piso de los calendarios: max(first_month, report_start_month).
    // Se dispara una vez al montar el dashboard de reportes; si falla, el piso
    // queda null y los calendarios se comportan como hoy (sin piso).
    //
    // Con el piso en mano, AJUSTA UNA SOLA VEZ el rango persistido (sessionStorage)
    // que haya quedado antes del piso — p.ej. el analista fijó el inicio en
    // 2025 y este tab traía un rango custom desde 2024. Se hace acá, ANTES de
    // `dataAvailabilityReady`, para que los tabs monten con el estado ya
    // coherente y su fetch inicial sea uno solo (ajustarlo después dispararía
    // los watchers de fetchSignature y una segunda consulta).
    // Botón "Exportar" del header: encola el export al mes de corte, sondea la
    // corrida hasta S/E y abre la URL prefirmada (una sola emisión, 60 s).
    async exportReport({ state, commit }) {
        if (state.exportState === EXPORT_RUNNING) return null;
        commit("SET_EXPORT_STATE", EXPORT_RUNNING);
        commit("SET_EXPORT_PROGRESS", null);
        try {
            const endDate = moment(state.endMonth).endOf("month").format("YYYY-MM-DD");
            const { data: started } = await startReportExport(endDate);
            const finished = await exportPolling.poll(
                () => getReportExportStatus(started.run_id).then((response) => response.data),
                { onTick: (status) => exportTick(commit, status) },
            );
            if (finished.status !== "S") {
                throw new ReportExportFailed(finished.error);
            }
            const { data: download } = await getReportExportDownload(started.run_id);
            downloadUrl(download.url, download.file_name);
            commit("SET_EXPORT_STATE", EXPORT_IDLE);
            commit("SET_EXPORT_PROGRESS", null);
            notificate(`<strong>Descarga iniciada</strong><br>${download.file_name}`,
                "is-success is-light");
            return download;
        } catch (error) {
            consoleHandlerError({ error, prefix: "[reportsFilters]", silent: true });
            notificate(`<strong>No se pudo exportar el reporte</strong><br>${exportErrorMessage(error)}`,
                "is-warning is-light");
            commit("SET_EXPORT_STATE", EXPORT_ERROR);
            commit("SET_EXPORT_PROGRESS", null);
            return null;
        }
    },
    async loadDataAvailability({ state, commit }) {
        try {
            const { data } = await getLedgerYears();
            const floor = resolveDataFirstMonth(data);
            commit("SET_DATA_FIRST_MONTH", floor);
            const patch = normalizeFiltersToFloor(state, floor);
            if ("endMonth" in patch) commit("SET_END_MONTH", patch.endMonth);
            if ("customRange" in patch) commit("SET_CUSTOM_RANGE", patch.customRange);
            if ("customYearRange" in patch) commit("SET_CUSTOM_YEAR_RANGE", patch.customYearRange);
            if ("selectedPeriodRange" in patch) {
                commit("SET_SELECTED_PERIOD_RANGE", patch.selectedPeriodRange);
            }
        } catch (e) {
            commit("SET_DATA_FIRST_MONTH", null);
        } finally {
            // Con o sin dato, los tabs pueden montar y fetchear: null = sin
            // piso (mismo comportamiento que antes del recorte).
            commit("SET_DATA_AVAILABILITY_READY", true);
        }
    },
};

// Piso visible = el más tardío entre el derivado de los documentos y el
// manual del analista; con uno solo presente gana ese; sin ninguno, null.
// Exportado para testearlo sin store.
export function resolveDataFirstMonth({ first_month, report_start_month } = {}) {
    const candidates = [first_month, report_start_month]
        .filter(Boolean)
        .map((iso) => moment(iso).toDate());
    if (!candidates.length) return null;
    return new Date(Math.max(...candidates.map((d) => d.getTime())));
}

// Qué hay que cambiar del rango persistido para que no quede nada ANTES del
// piso. Devuelve solo las claves a corregir (objeto vacío = nada que hacer):
//   - endMonth anterior al piso → vuelve al default (hoy): con ese mes ningún
//     rango predefinido puede mostrar datos.
//   - customRange que termina antes del piso → se descarta y el período vuelve
//     a "lastYear"; si solo empieza antes, su `from` sube al piso.
//   - customYearRange: mismo criterio por año calendario.
// Pura y exportada para testearla sin store.
export function normalizeFiltersToFloor(state, floor) {
    const patch = {};
    if (!floor) return patch;
    const floorMonth = moment(floor).startOf("month");
    if (!floorMonth.isValid()) return patch;

    if (moment(state.endMonth).isBefore(floorMonth)) {
        patch.endMonth = moment().toDate();
    }

    const range = state.customRange;
    if (range && range.from && range.to) {
        if (moment(range.to).endOf("month").isBefore(floorMonth)) {
            patch.customRange = null;
            if (state.selectedPeriodRange === "custom") patch.selectedPeriodRange = "lastYear";
        } else if (moment(range.from).isBefore(floorMonth)) {
            patch.customRange = { from: floorMonth.toDate(), to: range.to };
        }
    }

    const years = state.customYearRange;
    if (years && years.from && years.to) {
        const floorYear = floorMonth.clone().startOf("year");
        if (moment(years.to).endOf("year").isBefore(floorYear)) {
            patch.customYearRange = null;
        } else if (moment(years.from).isBefore(floorYear)) {
            patch.customYearRange = { from: floorYear.toDate(), to: years.to };
        }
    }
    return patch;
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
